import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchRequest, fetchSiblingRequests, fetchEntityChildren, fetchAttachmentBlob,
  searchEntities, fetchEntityByPk, approveRequest, rejectRequest, revertRequest,
  reopenRequest, lockRequest, unlockRequest, unlockRequestBeacon, previewConfidence,
  fetchAmministratoreSuggest, fetchAuditByRequest, fetchConversationByRequest,
  type PreviewConfidenceResponse, type RdiForceMatch, type RdiOperatorOverrides,
} from "../api/client";
import { StatusBadge } from "../lib/status";
import { DetailPageShell } from "../lib/DetailPageShell";
import { ActivityPanel, useActivity } from "../lib/ActivityPanel";
import { PracticeOutcome } from "../lib/PracticeOutcome";
import { ConversationThread } from "../lib/ConversationThread";
import { AgentEvidence } from "../lib/AgentEvidence";
import type {
  ProcessingRequest, EntityMatch, TicketMatch, EntitySearchResult,
  CorrectionItem, SiblingTicket, AmministratoreSuggestion, ConversationByRequest,
} from "../api/types";
import { ENTITY_FEEDBACK_CATEGORIES } from "../api/types";
import { MopRequestNotice } from "../components/MopRequestNotice";
import { isMopRequest } from "../helpers/requestFamily";
import { useTenantConfig } from "../hooks/useTenantConfig";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../auth/useAuth";
import { canWorkTickets } from "../auth/permissions";
import { getOriginalSenderFull, getCleanSubject, getOriginalDate } from "../helpers/emailMetadata";
import { stripPkPrefix } from "../helpers/entityUtils";
import { PreviewModal, fileIcon, buildPreviewData, resolveContentType } from "../components/AttachmentPreview";
import { RejectModal } from "../components/RejectModal";
import { ReprocessModal } from "../components/ReprocessModal";
import { ConfirmModal } from "../components/ConfirmModal";
import { CertificationTree, NO_CERTIFICATION, certificationGridPlacement } from "../components/CertificationTree";
import type { CertificationChoice } from "../components/CertificationTree";
import type { PreviewData } from "../components/AttachmentPreview";

/* ════════════════════════════════════════════════════════════════════════
   Types
   ════════════════════════════════════════════════════════════════════ */

type Correction = {
  entity_type: string;
  new_pk: string;
  label: string;
  fields?: Record<string, unknown>;
};

/* Entità della catena principale — una modifica richiede riprocesso */
const CHAIN_ENTITIES = new Set(["cliente", "edificio", "impianto"]);

/* ════════════════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════════════ */

function fmt(d: string) {
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(d));
  } catch { return d; }
}
function pct(s: number) { return `${(s * 100).toFixed(0)}%`; }
// Single consolidated priority→token mapping (alta/media/bassa), used everywhere
// priority is displayed so the same semantic color is never re-derived ad hoc.
function priorityColors(p: string): { bg: string; color: string } {
  if (p === "alta") return { bg: "var(--indaco-danger-light)", color: "var(--indaco-danger)" };
  if (p === "media") return { bg: "var(--indaco-warning-light)", color: "var(--indaco-warning)" };
  if (p === "bassa") return { bg: "var(--indaco-success-light)", color: "var(--indaco-success)" };
  return { bg: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)" };
}
// Thresholds intentionally match the pre-existing behavior (not <ConfidenceBadge>'s
// 0.8/0.5 cutoffs) — only the colors were retokenized, the conditional logic is unchanged.
function sCol(s: number) { return s >= .85 ? "var(--indaco-success)" : s >= .6 ? "var(--indaco-warning)" : "var(--indaco-danger)"; }
function sBg(s: number) { return s >= .85 ? "var(--indaco-success-light)" : s >= .6 ? "var(--indaco-warning-light)" : "var(--indaco-danger-light)"; }
function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

const ICON: Record<string, string> = {
  cliente: "business_center", edificio: "apartment",
  impianto: "settings", amministratore: "badge", chiamante: "person",
};
// No dedicated per-entity-type color map exists elsewhere (EntityRow/EntityCard derive
// their accent from match *state*, not entity type) — so each type is routed to the
// closest matching Indaco token instead of an arbitrary hex.
const COLOR: Record<string, string> = {
  cliente: "var(--indaco-gray-700)", edificio: "var(--indaco-brand-primary)",
  impianto: "var(--indaco-warning)", amministratore: "var(--indaco-success)", chiamante: "var(--indaco-info)",
};

function entityDetail(key: string, f: Record<string, unknown> | undefined): string {
  if (!f) return "";
  if (key === "edificio") return [f.indirizzo, f.comune].filter(Boolean).join(" · ");
  if (key === "impianto") return [f.desc_tipo, f.descrizione].filter(Boolean).join(" — ");
  // `email_certificata` e' la MASCHERA dell'indirizzo su cui il numero ha
  // dimostrato di avere accesso (migration 064): non e' un indirizzo, e infatti
  // il backend la tiene in un campo diverso da `email` proprio perche' non
  // finisca nel JSON verso il gestionale. Qui invece va mostrata: su una
  // pratica nata in chat e' spesso l'unico appiglio che l'operatore ha.
  if (key === "chiamante") return [f.telefono, f.email, f.email_certificata].filter(Boolean).join(" · ");
  if (key === "amministratore") return String(f.ragione_sociale || f.nominativo || "");
  if (key === "cliente") return String(f.ragsoc || "");
  return "";
}

function displayFilename(filename: string): { label: string; isSub: boolean } {
  if (filename.includes("__sub__")) return { label: filename.split("__sub__").pop() || filename, isSub: true };
  return { label: filename, isSub: false };
}

/* ════════════════════════════════════════════════════════════════════════
   Attachments (internal component)
   ════════════════════════════════════════════════════════════════════ */

function Attachments({ requestId, attachments }: {
  requestId: string;
  attachments: { filename: string; content_type: string; size_bytes: number; extracted_text?: string }[];
}) {
  const [previewing, setPreviewing] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  async function handleClick(att: { filename: string; content_type: string; extracted_text?: string }) {
    setError(null); setRenderError(null); setLoading(att.filename);
    const ct = resolveContentType(att.content_type, att.filename);
    const hasBinaryPreview = ct.startsWith("image/") || ct === "application/pdf" || ct.startsWith("video/") || ct.startsWith("audio/");
    // Per file con preview binaria (immagini, PDF, video, audio) → fetch il file reale.
    // Per .eml e sub-allegati senza preview binaria → mostra il testo estratto.
    if (att.extracted_text != null && !hasBinaryPreview) {
      const { label } = displayFilename(att.filename);
      setPreviewing({ url: "", contentType: "text/plain", filename: label, textContent: att.extracted_text || "(nessun testo estratto)" });
      setLoading(null);
      return;
    }
    try {
      const { url, contentType: rawCT } = await fetchAttachmentBlob(requestId, att.filename);
      const { preview, renderError: re } = await buildPreviewData(url, rawCT, att.filename);
      if (re) setRenderError(re);
      setPreviewing(preview);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Errore";
      setError(msg.includes("404") ? "Allegato non disponibile — riprocessare per recuperarlo." : msg);
    } finally { setLoading(null); }
  }
  function close() { if (previewing) URL.revokeObjectURL(previewing.url); setPreviewing(null); setRenderError(null); }

  return (
    <div style={{ marginTop: 8 }}>
      <div className="text-muted fw-semibold" style={{ fontSize: "var(--font-size-xs)", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>attach_file</span>
        Allegati ({attachments.length})
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {attachments.map((a, i) => {
          const { label, isSub } = displayFilename(a.filename);
          return (
            <button key={i} className="btn btn-outline btn-sm" disabled={loading === a.filename} onClick={() => handleClick(a)}
              title={isSub ? `Sub-allegato: ${a.filename.split("__sub__")[0]}` : a.filename}
              style={{
                gap: 3, fontSize: "var(--font-size-xs)",
                border: isSub ? "1px dashed var(--indaco-brand-primary)" : undefined,
                background: isSub ? "var(--indaco-brand-primary-light)" : undefined,
              }}>
              {isSub && <span className="material-symbols-outlined" style={{ fontSize: 11, color: "var(--indaco-brand-primary)" }}>subdirectory_arrow_right</span>}
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{fileIcon(a.content_type, a.filename)}</span>
              <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
              {a.size_bytes > 0 && <span className="text-muted" style={{ fontSize: "var(--font-size-xs)" }}>({formatBytes(a.size_bytes)})</span>}
              {loading === a.filename && <span className="spinner" style={{ width: 10, height: 10 }} />}
            </button>
          );
        })}
      </div>
      {error && <div className="text-sm" style={{ color: "var(--indaco-danger)", marginTop: 3 }}>{error}</div>}
      {previewing && createPortal(<PreviewModal data={previewing} renderError={renderError} onClose={close} />, document.body)}
    </div>
  );
}

/* ReasonPicker removed — motivo correzione chiesto nella barra azioni */

/* ════════════════════════════════════════════════════════════════════════
   ConversationSection — sezione supplementare (Pacchetto E): transcript +
   decisioni per le pratiche di origine conversazionale. Renderizzata SOLO
   quando `extraction.metadata.source === "conversation"` (vedi Review3()
   sotto) — per una pratica email-normale questo componente non viene
   nemmeno montato, quindi nessuna regressione visiva sul resto della pagina.
   ════════════════════════════════════════════════════════════════════ */

/** Il canale come lo legge un operatore, non come lo scrive il database. */
function canaleLeggibile(canale: string): string {
  const mappa: Record<string, string> = { whatsapp: "WhatsApp", test: "Prova" };
  return mappa[canale] ?? canale;
}

/* Esportata per il test: e' il blocco che concentra cio' che l'operatore
   legge di una conversazione, ed e' dove stavano i reason code grezzi. */
export function ConversationSection({ data, loading, error, onViewSession }: {
  data: ConversationByRequest | null; loading: boolean; error: string | null;
  onViewSession: (sessionId: string, channel: string) => void;
}) {
  return <section className="card" style={{ padding: "var(--space-3)", marginBottom: "var(--space-3)" }}>
    <h2 className="text-sm">Conversazione</h2>
    {loading && !data && <div className="spinner" />}
    {error && <p role="alert">{error}</p>}
    {data && <>
      <p className="text-sm">{canaleLeggibile(data.channel)} · Dati confermati, revisione operatore richiesta</p>
      <ConversationThread messages={data.transcript} maxHeight="240px" showMeta={false} />
      {data.locations?.map((location, index) => <p key={index}>Posizione dichiarata: {location.name} {location.address} ({location.latitude}, {location.longitude})</p>)}
      {data.request_id && data.media?.map(media => <AgentEvidence key={media.id} media={media} requestId={data.request_id!} />)}
      <p className="text-muted text-xs">Controllo duplicati differito. I riferimenti a ticket precedenti sono da verificare.</p>
      <button className="btn btn-outline" onClick={() => onViewSession(data.session_id, data.channel)}>Vedi conversazione completa</button>
    </>}
  </section>;
}

/* ════════════════════════════════════════════════════════════════════════
   CausaleBadge
   ════════════════════════════════════════════════════════════════════ */

function CausaleBadge({ causale, numeroRiferimento, ticketAssociabili, ticketOriginale, isPreview, previewLoading }: {
  causale: string;
  numeroRiferimento?: string;
  ticketAssociabili: Array<{
    idchiamata: number; refimpianto?: string; motivo: string; dataora: string;
    stato: number; similarity: number; similarity_method: string;
  }>;
  ticketOriginale?: { tipo?: string; idchiamata?: number; request_id?: string; refimpianto?: string; motivo?: string; dataora?: string } | null;
  isPreview: boolean;
  previewLoading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const top = ticketAssociabili[0];

  // Rebuilt on the shared .alert-* variants (success/danger/warning) instead of a
  // bespoke hex config — variant maps 1:1 to the previous bg/border/color triple.
  const cfg: Record<string, { variant: "success" | "danger" | "warning"; icon: string; label: string }> = {
    apertura: { variant: "success", icon: "add_circle", label: "Nuovo ticket" },
    aggiornamento: { variant: "danger", icon: "update", label: "Aggiornamento ticket esistente" },
    escalation_ticket_esistente: { variant: "warning", icon: "warning", label: "Escalation: ticket esistente" },
    duplicato_allarme: { variant: "danger", icon: "notifications_off", label: "Allarme duplicato (ignorato)" },
  };
  const c = cfg[causale] || cfg.apertura;
  const hasSuggestions = causale === "apertura" && ticketAssociabili.length > 0;
  // AQS non verificabile: apertura con riferimento AQS Hxxxxx ma non trovato in ricerca_chiamate
  const hasUnverifiedAqs = causale === "apertura" && !!numeroRiferimento && /^AQS\s+H\d{4,}/i.test(numeroRiferimento);
  const display = hasSuggestions
    ? { variant: "warning" as const, icon: "info", label: `Nuovo ticket — ${ticketAssociabili.length} possibile/i duplicato/i` }
    : hasUnverifiedAqs
      ? { variant: "warning" as const, icon: "warning", label: "Possibile aggiornamento — rif. AQS non verificabile in archivio" }
      : c;

  // Etichetta ticket originale: per gestionale mostra #idchiamata, per allarme mostra impianto
  const origLabel = ticketOriginale
    ? ticketOriginale.tipo === "ticket_gestionale" && ticketOriginale.idchiamata
      ? `Aggiorna ticket #${ticketOriginale.idchiamata}`
      : ticketOriginale.tipo === "allarme_precedente" && ticketOriginale.refimpianto
        ? `Aggiorna allarme precedente su ${ticketOriginale.refimpianto}`
        : null
    : null;

  return (
    <div className={`alert alert-${display.variant}`} style={{ marginBottom: 8, flexDirection: "column", alignItems: "stretch", fontSize: "var(--font-size-sm)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{display.icon}</span>
        <span style={{ fontWeight: 700 }}>{display.label}</span>
        {isPreview && !previewLoading && <span className="badge badge-info badge-sm">PREVIEW</span>}
        {previewLoading && <span className="spinner" style={{ width: 10, height: 10 }} />}
        {numeroRiferimento && <span className="badge badge-neutral badge-sm">Rif: {numeroRiferimento}</span>}
        {top && !origLabel && <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600 }}>#{top.idchiamata}{top.similarity != null ? ` · ${(top.similarity * 100).toFixed(0)}%` : ""}</span>}
        {ticketAssociabili.length > 0 && (
          <button type="button" className="btn-link" onClick={() => setOpen(o => !o)}
            style={{ fontWeight: 600, gap: 2, marginLeft: origLabel ? "auto" : undefined }}>
            {open ? "Nascondi" : "Dettagli"}
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{open ? "expand_less" : "expand_more"}</span>
          </button>
        )}
      </div>
      {/* Ticket originale: riga dedicata */}
      {origLabel && (
        <div style={{ marginTop: 4, padding: "4px 8px", background: "rgba(255,255,255,0.7)", borderRadius: 4, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>link</span>
          <span style={{ fontWeight: 700 }}>{origLabel}</span>
          {ticketOriginale?.motivo && (
            <span title={ticketOriginale.motivo} style={{ color: "var(--indaco-gray-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>— {ticketOriginale.motivo}</span>
          )}
          {ticketOriginale?.dataora && (
            <span style={{ fontSize: 10, color: "var(--indaco-gray-600)", flexShrink: 0 }}>{(ticketOriginale.dataora ?? "").slice(0, 10)}</span>
          )}
        </div>
      )}
      {open && ticketAssociabili.map((t, idx) => (
        <div key={t.idchiamata ?? idx} style={{
          marginTop: 3, padding: "4px 8px", background: "rgba(255,255,255,0.6)", borderRadius: 4,
          fontSize: 10.5, display: "grid", gridTemplateColumns: "55px 80px 65px minmax(0,1fr)", gap: 5, alignItems: "center",
        }}>
          <span style={{ fontWeight: 700 }}>#{t.idchiamata}</span>
          <span style={{ color: "var(--indaco-gray-600)" }}>{(t.dataora ?? "").slice(0, 10)}</span>
          <span style={{ fontWeight: 600, color: t.similarity >= .65 ? "var(--indaco-danger)" : t.similarity >= .50 ? "var(--indaco-warning)" : "var(--indaco-gray-600)" }}>
            {(t.similarity * 100).toFixed(0)}%
          </span>
          <span title={t.motivo} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--indaco-gray-800)" }}>{t.motivo}</span>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   REASONING PANEL — narrativa discorsiva del ragionamento AI
   ════════════════════════════════════════════════════════════════════ */

interface RdiReasoning {
  detect_tickets?: string | null;
  sub_type?: string | null;
  priorita?: string | null;
  chiamante?: string | null;
}

function buildNarrative(
  em: Record<string, any>,
  analysis: Record<string, unknown> | null,
  extracted: Record<string, Record<string, unknown>> | null,
  causale: string,
  ticketAssociabili: any[],
  ticketOriginale: any,
  rdiReasoning?: RdiReasoning | null,
): string[] {
  // ── Righe Tipo 2: causale/dedup ─────────────────────────────────────────
  const causaleLines: string[] = [];
  if (causale === "aggiornamento") {
    if (ticketOriginale) {
      const ref = ticketOriginale.idchiamata || ticketOriginale.request_id?.slice(0, 8) || "";
      causaleLines.push(`Questa email è un aggiornamento del ticket #${ref}${ticketOriginale.motivo ? ` ("${ticketOriginale.motivo.slice(0, 60)}")` : ""}.`);
    } else {
      causaleLines.push("L'email si riferisce a un ticket esistente — classificata come aggiornamento.");
    }
  } else if (causale === "duplicato_allarme") {
    causaleLines.push("Allarme duplicato: un allarme identico sullo stesso impianto è stato registrato di recente.");
  } else if (causale === "escalation_ticket_esistente") {
    causaleLines.push("Ho trovato un ticket simile già aperto — classificata come escalation.");
  } else if (causale === "apertura") {
    if (ticketAssociabili.length > 0) {
      causaleLines.push(`Classificata come nuova apertura, ma ho trovato ${ticketAssociabili.length} ticket simil${ticketAssociabili.length === 1 ? "e" : "i"} che potrebbe${ticketAssociabili.length === 1 ? "" : "ro"} essere collegat${ticketAssociabili.length === 1 ? "o" : "i"}.`);
    } else {
      causaleLines.push("Nessun ticket esistente trovato — classificata come nuova apertura.");
    }
  }

  // ── Ragionamento reale dei nodi LangGraph ────────────────────────────────
  const hasRdi = rdiReasoning &&
    (rdiReasoning.detect_tickets || rdiReasoning.sub_type ||
     rdiReasoning.priorita || rdiReasoning.chiamante);

  if (hasRdi) {
    const lines: string[] = [];
    if (rdiReasoning!.detect_tickets) lines.push(rdiReasoning!.detect_tickets);
    if (rdiReasoning!.sub_type)       lines.push(rdiReasoning!.sub_type);
    if (rdiReasoning!.priorita)       lines.push(rdiReasoning!.priorita);
    if (rdiReasoning!.chiamante)      lines.push(rdiReasoning!.chiamante);
    return [...lines, ...causaleLines];
  }

  // ── Fallback per le pratiche storiche prive di reasoning ─────────────────
  const lines: string[] = [];
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const exEd = extracted?.edificio ?? {};
  const exImp = extracted?.impianto ?? {};
  const exChi = extracted?.chiamante ?? {};
  const exCli = extracted?.cliente ?? {};

  // 1. What was extracted from the email
  const extractedParts: string[] = [];
  if (exCli.ragsoc) extractedParts.push(`il cliente "${exCli.ragsoc}"`);
  if (exEd.indirizzo || exEd.descrizione || exEd.comune) {
    const addr = [exEd.indirizzo, exEd.comune].filter(Boolean).join(", ");
    const desc = exEd.descrizione ? `"${exEd.descrizione}"` : "";
    extractedParts.push(`l'edificio ${[desc, addr].filter(Boolean).join(" in ")}`);
  }
  if (exImp.desc_tipo || exImp.descrizione) {
    extractedParts.push(`un impianto di tipo ${exImp.desc_tipo || ""}${exImp.descrizione ? ` (${exImp.descrizione})` : ""}`);
  }
  if (exChi.chiamante || exChi.email) {
    extractedParts.push(`il chiamante ${exChi.chiamante || ""}${exChi.email ? ` <${exChi.email}>` : ""}`);
  }
  if (extractedParts.length > 0) {
    lines.push(`Dall'email ho estratto: ${extractedParts.join("; ")}.`);
  }
  const numRef = analysis?.numero_riferimento as string | undefined;
  if (numRef) lines.push(`Ho trovato il riferimento "${numRef}".`);

  // 2. Building match
  const edMatch = em.edificio;
  if (edMatch?.best?.entity?.pk && edMatch.match_method !== "none") {
    const method = edMatch.match_method || edMatch.best.method;
    const meta = edMatch.best.metadata || {};
    const score = edMatch.best.score ?? 0;
    const edPk = stripPkPrefix(edMatch.best.entity.pk);
    const edDesc = edMatch.best.display_label || edMatch.best.entity.fields?.descrizione || "";
    const edAddr = edMatch.best.entity.fields?.indirizzo || "";
    const alts = (edMatch.alternatives ?? []).length;

    if (method === "learned") {
      lines.push(`L'edificio ${edPk} ("${edDesc}"${edAddr ? `, ${edAddr}` : ""}) è stato riconosciuto da un feedback precedente (confidenza ${pct(score)}).`);
    } else if (method === "exact") {
      lines.push(`Ho trovato un match esatto per l'edificio: ${edPk} — "${edDesc}"${edAddr ? ` in ${edAddr}` : ""}.`);
    } else if (method === "fuzzy") {
      lines.push(`Ho cercato per similitudine testuale e il miglior risultato è ${edPk} — "${edDesc}"${edAddr ? ` in ${edAddr}` : ""} (similitudine ${pct(score)}).`);
      if (alts > 0) lines.push(`C'erano ${alts} alternative con score vicino${score <= 0.56 ? " — la confidenza è bassa perché i candidati sono troppo simili tra loro" : ""}.`);
    } else if (method === "custom_rule") {
      if (meta.rule === "filter_edifici_by_cliente_estratto") {
        lines.push(`Ho identificato il cliente "${meta.client_ragsoc || ""}" (similitudine ${pct(meta.client_sim ?? 0)}) e filtrato gli edifici associati. Il migliore è ${edPk} — "${edDesc}".`);
      } else if (meta.rule === "match_edificio_by_impianto_codice") {
        lines.push(`Dal codice impianto nel riferimento, sono risalito all'edificio ${edPk} — "${edDesc}".`);
      } else if (meta.rule === "match_edificio_by_amministratore_estratto") {
        lines.push(`Ho trovato l'amministratore nel testo e filtrato gli edifici gestiti. Risultato: ${edPk} — "${edDesc}".`);
      } else if (meta.rule === "match_by_numero_riferimento") {
        const src = meta.source === "processing_requests" ? "un ticket precedente" : "lo storico chiamate";
        lines.push(`Tramite il numero di riferimento, ho trovato l'edificio ${edPk} — "${edDesc}" in ${src}.`);
      } else {
        lines.push(`Una regola specifica ha identificato l'edificio ${edPk} — "${edDesc}".`);
      }
      if (alts > 0) lines.push(`Scartate ${alts} alternative con score inferiore.`);
    } else if (method === "vector") {
      lines.push(`Ho usato la ricerca semantica (vettoriale) e il risultato più vicino è ${edPk} — "${edDesc}" (similitudine ${pct(score)}).`);
    } else {
      lines.push(`Edificio trovato con metodo "${method}": ${edPk} — "${edDesc}" (${pct(score)}).`);
    }
  } else if (edMatch) {
    lines.push("Non ho trovato un edificio corrispondente nell'anagrafica.");
  }

  // Impianto
  const impMatch = em.impianto;
  if (impMatch?.best?.entity?.pk && impMatch.match_method !== "none") {
    const method = impMatch.match_method || impMatch.best.method;
    const meta = impMatch.best.metadata || {};
    const impPk = stripPkPrefix(impMatch.best.entity.pk);
    const impDesc = impMatch.best.display_label || impMatch.best.entity.fields?.descrizione || "";
    const alts = (impMatch.alternatives ?? []).length;

    if (method === "impianto_unico" || method === "custom_rule:impianto_unico") {
      lines.push(`L'edificio ha un solo impianto (${impPk}), quindi l'ho selezionato automaticamente.`);
    } else if (method === "learned") {
      lines.push(`L'impianto ${impPk} ("${impDesc}") è stato riconosciuto da un feedback precedente.`);
    } else if (method === "custom_rule:condominio" || method === "regola_centrale_termica") {
      lines.push(`Dal contenuto dell'email ho identificato l'impianto ${impPk} ("${impDesc}") tramite regola ${method === "custom_rule:condominio" ? "condominio/inquilino" : "centrale termica"}.`);
    } else if (method === "content_match") {
      lines.push(`Ho confrontato la descrizione dell'email con gli impianti dell'edificio. Il più coerente è ${impPk} — "${impDesc}".`);
    } else if (method === "fuzzy") {
      lines.push(`Per similitudine testuale, l'impianto migliore è ${impPk} — "${impDesc}".`);
      if (alts > 0) lines.push(`${alts} impianti alternativi scartati.`);
    } else {
      lines.push(`Impianto ${impPk} ("${impDesc}") trovato con metodo "${method}".`);
    }
  } else if (impMatch && edMatch?.best?.entity?.pk) {
    lines.push("Non ho individuato l'impianto specifico tra quelli dell'edificio.");
  }

  // Chiamante
  const chiMatch = em.chiamante;
  if (chiMatch?.best?.entity?.pk && chiMatch.match_method !== "none") {
    const method = chiMatch.match_method || chiMatch.best.method;
    const chiLabel = chiMatch.best.display_label || "";
    if (method === "match_by_email" || method === "email_match") {
      lines.push(`Il chiamante "${chiLabel}" è stato identificato dall'indirizzo email.`);
    } else if (method === "sender_fallback" || method === "fallback_sender") {
      lines.push(`Non ho trovato il chiamante nell'anagrafica. Ho creato un nuovo contatto dai dati dell'email.`);
    } else if (method === "learned") {
      lines.push(`Il chiamante "${chiLabel}" è stato riconosciuto da un feedback precedente.`);
    } else if (method === "conversation_certified_caller") {
      // Non è un match sull'anagrafica ed è deliberato dirlo: qui l'identità
      // non è stata dedotta da un mittente, è stata dimostrata.
      const provenienza = String(chiMatch.best.entity.fields?.provenienza_nome || "");
      const telefono = String(chiMatch.best.entity.fields?.telefono || "");
      const dove = provenienza === "contatto_verificato"
        ? "il nome è quello del contatto che ha verificato la casella"
        : provenienza === "anagrafica_chiamanti"
          ? "il nome è quello che l'anagrafica associa a questo numero"
          : "il nome non risulta né fra i contatti verificati né in anagrafica";
      lines.push(`Il chiamante scrive da un numero certificato (${telefono}): ${dove}.`);
    } else {
      lines.push(`Chiamante "${chiLabel}" identificato (${method}).`);
    }
  }

  // Cliente e amministratore (derived)
  const cliMatch = em.cliente;
  if (cliMatch?.best?.entity?.pk && cliMatch.match_method !== "none") {
    const cliLabel = cliMatch.best.display_label || cliMatch.best.entity.fields?.ragsoc || "";
    const method = cliMatch.match_method || cliMatch.best.method;
    if (method === "fk_lookup") {
      lines.push(`Il cliente "${cliLabel}" è derivato direttamente dall'edificio.`);
    } else {
      lines.push(`Cliente: "${cliLabel}".`);
    }
  }

  return [...lines, ...causaleLines];
}

// Esportato per il test: e' il solo posto in cui l'operatore legge il PERCHE'
// di un match, e senza export il difetto 5 sarebbe verificabile solo col
// browser (`tests/e2e/`), non anche da un test veloce.
export function ReasoningPanel({ em, analysis, extracted, causale, ticketAssociabili, ticketOriginale, rdiReasoning, validationErrors }: {
  em: Record<string, any>; analysis: Record<string, unknown> | null;
  extracted: Record<string, Record<string, unknown>> | null;
  causale: string; ticketAssociabili: any[]; ticketOriginale: any;
  rdiReasoning?: RdiReasoning | null;
  // Diagnostica della pipeline (`extraction.metadata.rdi_validation_errors`):
  // oggi la alimenta la marcatura del perimetro certificato (difetto 5). Il
  // dato arrivava dall'API e non veniva letto da nessun componente, quindi
  // l'operatore non sapeva PERCHE' un match fosse da verificare.
  validationErrors?: string[];
}) {
  const [open, setOpen] = useState(true);
  const lines = buildNarrative(em, analysis, extracted, causale, ticketAssociabili, ticketOriginale, rdiReasoning);
  const avvisi = (validationErrors ?? []).filter(v => typeof v === "string" && v.trim());
  if (lines.length === 0 && avvisi.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <button
        className="card-header-accent card-header-accent-brand"
        style={{ width: "100%", cursor: "pointer", border: "none", fontSize: "var(--font-size-xs)", fontWeight: 700, color: "var(--indaco-brand-primary)", textTransform: "uppercase", letterSpacing: ".04em" }}
        onClick={() => setOpen(o => !o)}>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>psychology</span>
        Ragionamento AI
        <span className="material-symbols-outlined" style={{ fontSize: 13, marginLeft: "auto", transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }}>expand_more</span>
      </button>
      {open && (
        <div className="text-sm" style={{ lineHeight: 1.7, color: "var(--indaco-gray-800)" }}>
          {avvisi.map((avviso, i) => (
            <p key={`avviso-${i}`} style={{ margin: "0 0 6px", color: "var(--indaco-warning)", fontWeight: 600 }}>
              {/* `aria-hidden`: la legatura dell'icona ("warning") fa parte del
                  contenuto testuale, quindi senza questo uno screen reader
                  leggerebbe la parola "warning" attaccata al motivo. */}
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 14, verticalAlign: "-2px", marginRight: 4 }}>warning</span>
              {avviso}
            </p>
          ))}
          {lines.map((line, i) => (
            <p key={i} style={{ margin: "0 0 6px" }}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════ */

const LOCK_HB_MS = 2 * 60 * 1000;

/* ── Preset larghezza pannello EMAIL (punto 8): stesso controllo Ampio/
   Equilibrato/Compatto dei DDT (SPLIT_PRESETS in DdtDetail), ma qui regola la
   quota della colonna DESTRA (EMAIL ORIGINALE) invece del PDF. "Ampio" = email
   grande (per leggere email lunghe), "Compatto" = email stretta (focus su
   ticket/matching). La colonna sinistra prende il resto. Persistito in
   localStorage. */
type Review3Split = "ampio" | "equilibrato" | "compatto";
const REVIEW3_SPLITS: Record<Review3Split, { label: string; emailPct: number }> = {
  ampio: { label: "Ampio", emailPct: 55 },
  equilibrato: { label: "Equilibrato", emailPct: 42 },
  compatto: { label: "Compatto", emailPct: 30 },
};
const REVIEW3_SPLIT_ORDER: Review3Split[] = ["ampio", "equilibrato", "compatto"];
const REVIEW3_SPLIT_KEY = "review3EmailSplitPreset";

/* ════════════════════════════════════════════════════════════════════════
   Certificazione del chiamante

   La proposta non e' piu' un pannello a tre radio: e' la tabella ad albero
   `CertificationTree` (cliente → edificio → impianto), che pubblica qui la
   scelta corrente. La pagina la tiene in stato perche' la scelta viaggia nel
   **payload dell'approvazione**, e quindi vale per entrambi i percorsi:
   approvazione diretta e approvazione dopo correzione+riprocesso.

   Due differenze rispetto a prima, volute: il pannello si mostra anche quando
   il numero e' gia' certificato (prima si nascondeva, e un numero certificato
   su 1 edificio su 61 non era piu' governabile), e la selezione puo'
   **ridurre**, non solo estendere.
   ════════════════════════════════════════════════════════════════════ */


export function Review3({ origin }: { origin?: "mop" } = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { entityTypes, config } = useTenantConfig();
  const queuePath = origin === "mop" ? "/mop/requests" : "/";
  const detailBasePath = origin === "mop" ? "/mop/requests" : "/review3";
  const { addToast } = useToast();
  const { user } = useAuth();
  const canWork = canWorkTickets(user?.roles ?? []);

  /* ── Core state ── */
  const [req, setReq] = useState<ProcessingRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const redirectToMop = Boolean(!origin && req && req.id === id && config?.tenant_id === "casadei" && config.mop_enabled && isMopRequest(req));
  const lockReady = Boolean(req?.id === id && config);
  useEffect(() => {
    if (redirectToMop && req) {
      navigate(`/mop/requests/${req.id}`, { replace: true });
    }
  }, [redirectToMop, req, navigate]);
  const [err, setErr] = useState<string | null>(null);
  const [operatorOverrides, setOperatorOverrides] = useState<RdiOperatorOverrides>({});

  /* ── Workflow ── */
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [lockOwner, setLockOwner] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [emailSplit, setEmailSplit] = useState<Review3Split>(() => {
    const s = localStorage.getItem(REVIEW3_SPLIT_KEY);
    return s && (REVIEW3_SPLIT_ORDER as string[]).includes(s) ? (s as Review3Split) : "equilibrato";
  });
  const selectEmailSplit = useCallback((p: Review3Split) => {
    setEmailSplit(p);
    localStorage.setItem(REVIEW3_SPLIT_KEY, p);
  }, []);

  /* ── Corrections ── */
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionNote, setCorrectionNote] = useState("");

  /* ── Search ── */
  const [editingEntity, setEditingEntity] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<EntitySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  /* ── Cascade derived state ── */
  const [impiantiList, setImpiantiList] = useState<EntitySearchResult[]>([]);
  const [impiantiLoading, setImpiantiLoading] = useState(false);
  const [derivedAdmin, setDerivedAdmin] = useState<{ pk: string; label: string; fields?: Record<string, unknown> } | null>(null);
  const [derivedCliente, setDerivedCliente] = useState<{ pk: string; label: string; fields?: Record<string, unknown> } | null>(null);
  const [chiamanteWarning, setChiamanteWarning] = useState<string | null>(null);

  /* ── Amministratore HubSpot (Fase 2) ── */
  const [adminResults, setAdminResults] = useState<AmministratoreSuggestion[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);
  // PK (con prefisso cod_az) degli edifici gestiti dall'admin SCELTO manualmente
  // (null = admin derivato dall'edificio o nessuna scelta). Serve a ricalcolare
  // la coerenza se l'operatore cambia edificio dopo aver scelto l'admin.
  const [adminEdificiPks, setAdminEdificiPks] = useState<string[] | null>(null);
  // Coerenza admin↔edificio: true = edificio ∈ edifici gestiti; false = mismatch;
  // null = non valutabile (nessun admin, admin senza codice locale, o edificio ignoto).
  const [adminCoherent, setAdminCoherent] = useState<boolean | null>(null);

  /* ── Preview ── */
  const [preview, setPreview] = useState<PreviewConfidenceResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [siblings, setSiblings] = useState<SiblingTicket[]>([]);
  const lockRef = useRef<ReturnType<typeof setInterval>>();
  // Ref always current: clientePk effettivo (corrections > derivedCliente > req.matches).
  // Usato da doSearch e useEffect preload per evitare stale-closure su derivedCliente async.
  const effectiveClientePkRef = useRef<string | undefined>(undefined);

  /* ── Other entities collapsed ── */
  const [showOther, setShowOther] = useState(false);
  /* ── Reprocess completed flag: cleared on new corrections ── */
  const [reprocessed, setReprocessed] = useState(false);

  /* ── Conversazione (Pacchetto E) — sezione supplementare, solo per pratiche
     di origine conversazionale. Fetch separato dal dettaglio pratica: la
     pagina lavora anche senza, e chiamare `/conversations/by-request/{id}`
     per OGNI pratica (comprese quelle email) farebbe solo 404 inutili. */
  const [conversationData, setConversationData] = useState<ConversationByRequest | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);

  /* ════════════════════════════════════════════════════════════════════
     Data loading
     ════════════════════════════════════════════════════════════════ */

  const refresh = useCallback(() => {
    if (!id) return;
    fetchRequest(id).then(r => { setReq(r); setErr(null); }).catch(e => setErr(e.message));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    localStorage.setItem("lastVisitedId", id);
    setLoading(true);
    fetchRequest(id).then(r => { setReq(r); setErr(null); }).catch(e => setErr(e.message)).finally(() => setLoading(false));
    fetchSiblingRequests(id).then(setSiblings).catch(() => setSiblings([]));
  }, [id]);

  /* Origine conversazionale (Pacchetto E) — **unica** derivazione della pagina.
     Il path autoritativo è quello del filtro backend `origin=conversation`,
     cioè `incoming_data->metadata->>source` (scritto da
     `src/conversation/materializer.py`). Prima si leggeva **solo**
     `extraction.metadata.source`, in due punti separati: verificato sul DB
     locale (14 pratiche conversazionali `casadei`, 2026-09-05) quel campo è
     valorizzato solo su 9 di esse — sulle 5 più recenti è assente e la pagina
     le trattava come email (niente sezione Conversazione). Si guarda quindi
     `incoming_data` per primo, con `extraction` come fallback.
     Sta qui in alto perché la usano sia l'effect della conversazione sia il
     montaggio di `CertificationTree`. */
  const isFromConversation =
    ((req?.incoming_data?.metadata as Record<string, unknown> | null | undefined)?.source
      ?? ((req?.extraction as Record<string, unknown> | null | undefined)?.metadata as Record<string, unknown> | null | undefined)?.source)
    === "conversation";

  /* Conversazione. Dipende da `req?.updated_at` come versione — stesso trucco
     usato sotto per `activity` — non da tutto `req`, per non rifare la fetch a
     ogni piccola modifica locale che non cambi l'origine della pratica. */
  useEffect(() => {
    if (!id || !isFromConversation) {
      setConversationData(null);
      setConversationError(null);
      return;
    }
    setConversationLoading(true);
    fetchConversationByRequest(id)
      .then((d) => { setConversationData(d); setConversationError(null); })
      .catch((e) => setConversationError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setConversationLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, req?.updated_at, isFromConversation]);

  /* Lock */
  useEffect(() => {
    if (!id || !canWork || !lockReady || redirectToMop) return;
    let mounted = true;
    async function acquire() {
      try {
        const { status, data } = await lockRequest(id!);
        if (!mounted) return;
        if (status === 423) { setLockOwner(data?.operator_name || data?.locked_by || "altro operatore"); setIsReadOnly(true); }
        else { setLockOwner(null); setIsReadOnly(false); }
      } catch { /* ignore */ }
    }
    acquire();
    lockRef.current = setInterval(acquire, LOCK_HB_MS);
    const onUnload = () => { try { unlockRequestBeacon(id!); } catch { /* */ } };
    window.addEventListener("beforeunload", onUnload);
    return () => { mounted = false; clearInterval(lockRef.current); window.removeEventListener("beforeunload", onUnload); unlockRequest(id!).catch(() => { }); };
  }, [id, canWork, lockReady, redirectToMop]);

  /* Preview confidence on corrections change */
  useEffect(() => {
    if (!id || !req) return;
    const chainCorr = corrections.filter(c => CHAIN_ENTITIES.has(c.entity_type));
    if (chainCorr.length === 0) { setPreview(null); return; }
    let aborted = false;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await previewConfidence(id, chainCorr.map(c => ({ entity_type: c.entity_type, new_pk: c.new_pk })));
        if (!aborted) setPreview(r);
      } catch { if (!aborted) setPreview(null); }
      finally { if (!aborted) setPreviewLoading(false); }
    }, 350);
    return () => { aborted = true; clearTimeout(handle); setPreviewLoading(false); };
  }, [corrections, id, req]);

  /* Load impianti + derive admin/cliente for initial edificio */
  useEffect(() => {
    if (!req) return;
    const m = (req.matches?.[0] ?? null)?.entity_matches ?? {};
    const edPk = m.edificio?.best?.entity?.pk;
    if (edPk) { loadImpiantiForEdificio(edPk, false); deriveFromEdificio(edPk); }
    const chiFields = m.chiamante?.best?.entity?.fields;
    if (chiFields) checkChiamanteCoherence(chiFields, edPk || null);
  }, [req]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Preload edifici list when opening the edificio edit panel with a known cliente */
  useEffect(() => {
    if (editingEntity !== "edificio") return;
    const clientePk = effectiveClientePkRef.current;
    if (!clientePk || searchResults.length > 0) return;
    setSearchLoading(true);
    fetchEntityChildren("cliente", clientePk, "edificio")
      .then(list => setSearchResults(list))
      .catch(() => {})
      .finally(() => setSearchLoading(false));
  }, [editingEntity]); // ref sempre aggiornato — nessuna stale-closure

  /* ════════════════════════════════════════════════════════════════════
     Cascade logic
     ════════════════════════════════════════════════════════════════ */

  const loadImpiantiForEdificio = useCallback(async (edificioPk: string, autoSelect: boolean): Promise<EntitySearchResult[]> => {
    setImpiantiLoading(true);
    try {
      const list = await fetchEntityChildren("edificio", edificioPk, "impianto");
      setImpiantiList(list);
      if (autoSelect && list.length === 1) {
        setCorrections(prev => [...prev.filter(c => c.entity_type !== "impianto"),
          { entity_type: "impianto", new_pk: list[0].pk, label: list[0].label, fields: list[0].fields }]);
      }
      return list;
    } catch { setImpiantiList([]); return []; }
    finally { setImpiantiLoading(false); }
  }, []);

  const deriveFromEdificio = useCallback(async (edificioPk: string) => {
    try {
      const ed = await fetchEntityByPk("edificio", edificioPk);
      const adminId = ed.fields?.amministratore_id;
      if (adminId) {
        try {
          const admin = await fetchEntityByPk("amministratore", String(adminId));
          setDerivedAdmin({ pk: admin.pk, label: admin.label || String(admin.fields?.ragione_sociale || admin.fields?.nominativo || ""), fields: admin.fields });
        } catch { setDerivedAdmin(null); }
      } else { setDerivedAdmin(null); }
      const clienteId = ed.fields?.codice_cliente;
      if (clienteId) {
        try {
          const cliente = await fetchEntityByPk("cliente", String(clienteId));
          setDerivedCliente({ pk: cliente.pk, label: cliente.label || String(cliente.fields?.ragsoc || ""), fields: cliente.fields });
        } catch { setDerivedCliente({ pk: String(clienteId), label: String(clienteId) }); }
      } else { setDerivedCliente(null); }
    } catch { setDerivedAdmin(null); setDerivedCliente(null); }
  }, []);

  const checkChiamanteCoherence = useCallback((fields: Record<string, unknown> | undefined, edificioPk: string | null) => {
    if (!fields || !edificioPk) { setChiamanteWarning(null); return; }
    const arr = (fields.edifici ?? fields.codice_edificio) as string[] | undefined;
    if (!arr || !Array.isArray(arr)) { setChiamanteWarning(null); return; }
    setChiamanteWarning(arr.includes(edificioPk) ? null : `Chiamante non associato all'edificio ${edificioPk}. La relazione verrà aggiunta all'approvazione.`);
  }, []);

  /* PK edificio attualmente identificato (correzione > match pipeline). Con prefisso cod_az. */
  const currentEdificioPk = useCallback((): string | null => {
    return getCorrection("edificio")?.new_pk
      ?? req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk
      ?? null;
  }, [corrections, req]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Coerenza admin↔edificio: edificio identificato ∈ edifici gestiti dall'admin.
     null se non valutabile (edifici sconosciuti o edificio non identificato). */
  function computeAdminCoherence(edificiPks: string[] | null, edificioPk: string | null): boolean | null {
    if (!edificiPks || !edificioPk) return null;
    return edificiPks.includes(edificioPk);
  }

  /* ════════════════════════════════════════════════════════════════════
     Correction helpers
     ════════════════════════════════════════════════════════════════ */

  function getCorrection(eKey: string) { return corrections.find(c => c.entity_type === eKey); }

  function applyCorrection(eKey: string, pk: string, label: string, fields?: Record<string, unknown>) {
    setCorrections(prev => [...prev.filter(c => c.entity_type !== eKey), { entity_type: eKey, new_pk: pk, label, fields }]);
    setEditingEntity(null); setSearchQ(""); setSearchResults([]);
    setReprocessed(false);
  }
  function clearCorrection(eKey: string) { setCorrections(prev => prev.filter(c => c.entity_type !== eKey)); }

  async function cascadeFromEdificio(edificioPk: string, label: string, toast?: string, fields?: Record<string, unknown>) {
    // Setta correzione edificio + cancella impianto senza chiudere il pannello
    setCorrections(prev => [
      ...prev.filter(c => c.entity_type !== "edificio" && c.entity_type !== "impianto"),
      { entity_type: "edificio", new_pk: edificioPk, label, fields },
    ]);
    setReprocessed(false);
    setSearchQ(""); setSearchResults([]);

    deriveFromEdificio(edificioPk);
    const chiCorr = getCorrection("chiamante");
    const chiFields = chiCorr ? undefined : (req?.matches?.[0]?.entity_matches?.chiamante?.best?.entity?.fields);
    checkChiamanteCoherence(chiFields, edificioPk);

    // Se l'operatore aveva scelto un admin manualmente, ricalcola la coerenza col
    // nuovo edificio; se invece l'admin è derivato (nessuna correzione), è coerente
    // per costruzione → nessun warning.
    if (getCorrection("amministratore") && adminEdificiPks) {
      setAdminCoherent(computeAdminCoherence(adminEdificiPks, edificioPk));
    } else {
      setAdminCoherent(null);
    }

    const imps = await loadImpiantiForEdificio(edificioPk, true);
    if (imps.length > 1) {
      // Più impianti: apri il pannello impianto per la selezione
      setEditingEntity("impianto");
      if (toast) addToast("info", `${toast} — ${imps.length} impianti, seleziona quello corretto.`);
    } else {
      setEditingEntity(null);
      if (toast) addToast("info", toast);
    }
  }

  function selectEntity(entityKey: string, result: EntitySearchResult) {
    if (entityKey === "edificio") {
      cascadeFromEdificio(result.pk, result.label, `Edificio cambiato: ${result.label}`, result.fields);
    } else if (entityKey === "impianto") {
      applyCorrection("impianto", result.pk, result.label, result.fields);
      const impEdificio = String(result.fields?.codice_edificio || "");
      const edCorr = getCorrection("edificio");
      const currentEdPk = edCorr ? edCorr.new_pk : (req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk || "");
      if (impEdificio && impEdificio !== currentEdPk) {
        fetchEntityByPk("edificio", impEdificio).then(ed => {
          cascadeFromEdificio(ed.pk, ed.label, `Impianto di edificio diverso — aggiornata la catena.`, ed.fields);
          applyCorrection("impianto", result.pk, result.label, result.fields);
        }).catch(() => { });
      }
    } else if (entityKey === "cliente") {
      setDerivedCliente({ pk: result.pk, label: result.label, fields: result.fields });
      // NOTE: do NOT call applyCorrection here — it resets editingEntity/searchResults.
      // Set the correction manually and then fire the cascade.
      setCorrections(prev => [...prev.filter(c => c.entity_type !== "cliente"), { entity_type: "cliente", new_pk: result.pk, label: result.label, fields: result.fields }]);
      setReprocessed(false);
      fetchEntityChildren("cliente", result.pk, "edificio").then(filtered => {
        if (filtered.length === 1) {
          cascadeFromEdificio(filtered[0].pk, filtered[0].label, `Cliente ${result.label} — edificio unico auto-selezionato.`);
        } else if (filtered.length > 1) {
          setSearchResults(filtered);
          setEditingEntity("edificio");
          setSearchQ("");
          addToast("info", `${filtered.length} edifici per ${result.label}. Seleziona l'edificio.`);
        } else {
          setEditingEntity(null); setSearchQ(""); setSearchResults([]);
          addToast("info", `Nessun edificio per ${result.label}.`);
        }
      }).catch(e => {
        setEditingEntity(null); setSearchQ(""); setSearchResults([]);
        addToast("error", `Errore caricamento edifici: ${(e as Error).message}`);
      });
    } else if (entityKey === "amministratore") {
      applyCorrection("amministratore", result.pk, result.label);
      setDerivedAdmin({ pk: result.pk, label: result.label, fields: result.fields });
    } else if (entityKey === "chiamante") {
      applyCorrection("chiamante", result.pk, result.label);
      const edCorr = getCorrection("edificio");
      const currentEdPk = edCorr ? edCorr.new_pk : (req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk || null);
      checkChiamanteCoherence(result.fields, currentEdPk);
    }
  }

  function handleResetAll() {
    setCorrections([]); setDerivedAdmin(null); setDerivedCliente(null); setChiamanteWarning(null); setReprocessed(false); setCorrectionReason(""); setCorrectionNote("");
    setAdminEdificiPks(null); setAdminCoherent(null); setAdminResults([]);
    const origEdPk = req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk;
    if (origEdPk) { loadImpiantiForEdificio(origEdPk, false); deriveFromEdificio(origEdPk); }
  }

  /* ════════════════════════════════════════════════════════════════════
     Entity search
     ════════════════════════════════════════════════════════════════ */

  async function doSearch(eKey: string, q: string) {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    const parentPk = eKey === "impianto"
      ? (corrections.find(c => c.entity_type === "edificio")?.new_pk
          ?? req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk)
      : eKey === "edificio"
      ? effectiveClientePkRef.current  // ref sempre aggiornato, immune a stale-closure
      : undefined;
    try { setSearchResults(await searchEntities(eKey, q, 12, parentPk)); } catch { setSearchResults([]); }
    finally { setSearchLoading(false); }
  }

  /* Ricerca amministratori su HubSpot (endpoint dedicato, non searchEntities). */
  async function doSearchAdmin(q: string) {
    if (q.trim().length < 2) { setAdminResults([]); return; }
    setAdminLoading(true);
    try { setAdminResults(await fetchAmministratoreSuggest(q)); } catch { setAdminResults([]); }
    finally { setAdminLoading(false); }
  }

  /* Scelta di un amministratore dal popover HubSpot: calcola la coerenza con
     l'edificio identificato e registra la correzione con i campi che finiranno
     nell'output (`coerente_con_edificio` incluso). L'admin senza codice locale
     non può essere scritto nell'output (force_matches richiede una pk). */
  function selectAdmin(a: AmministratoreSuggestion) {
    if (!a.codice_amministratore) {
      addToast("error", `"${a.name}" non ha un codice amministratore locale: non può essere collegato all'output.`);
      return;
    }
    const edPk = currentEdificioPk();
    const edificiPks = (a.edifici_gestiti ?? []).map(e => e.pk);
    const coherent = computeAdminCoherence(edificiPks, edPk);
    const fields: Record<string, unknown> = {
      name: a.name,
      ragione_sociale: a.name,
      partita_iva: a.partita_iva ?? "",
      codice_fiscale: a.codice_fiscale ?? "",
      hubspot_id: a.hubspot_id,
      coerente_con_edificio: coherent === true,
    };
    setAdminEdificiPks(edificiPks);
    setAdminCoherent(coherent);
    applyCorrection("amministratore", a.codice_amministratore, a.name, fields);
    setDerivedAdmin({ pk: a.codice_amministratore, label: a.name, fields });
    setAdminResults([]); setSearchQ("");
  }

  /* ════════════════════════════════════════════════════════════════════
     Actions
     ════════════════════════════════════════════════════════════════ */

  /* Force-match dell'amministratore per l'output: correzione manuale (con la
     coerenza calcolata in FE) oppure admin derivato dall'edificio (coerente per
     costruzione). Null se nessun admin risolto → output `amministratore: null`. */
  function buildAdminForceMatch(): Record<string, RdiForceMatch> | null {
    const corr = getCorrection("amministratore");
    if (corr?.new_pk) {
      return {
        amministratore: {
          pk: corr.new_pk,
          fields: corr.fields,
          operator_override: true,
        },
      };
    }
    if (derivedAdmin?.pk) {
      return { amministratore: { pk: derivedAdmin.pk, fields: { ...(derivedAdmin.fields ?? {}), coerente_con_edificio: true } } };
    }
    return null;
  }

  async function handleApprove() {
    if (!id) return; setActionLoading(true);
    try {
      await approveRequest(id, "", operatorOverrides, buildAdminForceMatch(), certifyCaller.payload);
      addToast("success", "Richiesta inviata alla coda"); navigate(queuePath);
    } catch (e) { addToast("error", `Errore: ${(e as Error).message}`); }
    finally { setActionLoading(false); }
  }

  async function handleReject(categories: string[], note: string) {
    if (!id) return; setActionLoading(true);
    try { await rejectRequest(id, { categories, note }); addToast("success", "Richiesta scartata"); navigate(queuePath); }
    catch (e) { addToast("error", `Errore: ${(e as Error).message}`); }
    finally { setActionLoading(false); setShowRejectModal(false); }
  }

  async function handleRevert() {
    if (!id) return; setActionLoading(true);
    try { await revertRequest(id); addToast("info", "Match originale ripristinato"); setCorrections([]); refresh(); }
    catch (e) { addToast("error", `Errore: ${(e as Error).message}`); }
    finally { setActionLoading(false); }
  }

  async function doReopen() {
    if (!id) return; setActionLoading(true); setShowReopenConfirm(false);
    try {
      const res = await reopenRequest(id);
      const purge = (res as any)?.output_purge;
      const pQ = purge?.queue_deleted ?? 0, pB = purge?.blobs_deleted ?? 0, errs = (purge?.errors ?? []).length;
      const msg = pQ > 0 || pB > 0 ? `Richiesta riaperta. Output rimosso (${pQ} msg, ${pB} blob).` : "Richiesta riaperta";
      errs > 0 ? addToast("error", `${msg} ${errs} errore/i.`) : addToast("info", msg);
      refresh();
    } catch (e) { addToast("error", `Errore: ${(e as Error).message}`); }
    finally { setActionLoading(false); }
  }

  function handleReprocessComplete() {
    // onComplete viene chiamato solo dopo approveRequest (forza match o invia alla coda)
    // → naviga via come handleApprove, non restare sulla pagina con solo "Riapri" visibile.
    setShowReprocessModal(false);
    addToast("success", "Richiesta inviata alla coda");
    navigate(queuePath);
  }

  /* ── Cronologia (audit) ──
     Caricata una volta e condivisa fra il riassunto d'esito in cima e la
     cronologia in fondo: due richieste darebbero, nell'istante fra le due
     risposte, due versioni della stessa pratica. `updated_at` come versione, così
     un'approvazione o un rifiuto la ricaricano. */
  const { entries: activity, loading: activityLoading } = useActivity(
    useCallback(() => fetchAuditByRequest(id!), [id]),
    req?.updated_at,
  );

  /* Scelta di certificazione del chiamante: solo su pratiche conversazionali.
     La pubblica `CertificationTree`; qui si conserva perche' viaggia nel
     payload di approve, per entrambi i percorsi (diretto e riprocesso). */
  const [certifyCaller, setCertifyCaller] = useState<CertificationChoice>(NO_CERTIFICATION);
  /* Il pannello aperto prende la larghezza piena sopra le due colonne: lo
     dichiara lui, la griglia si riadatta. Chiuso, la pagina resta com'e'. */
  const [certExpanded, setCertExpanded] = useState(false);

  /* ════════════════════════════════════════════════════════════════════
     Derived data
     ════════════════════════════════════════════════════════════════ */

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}><div className="spinner spinner-lg" /></div>;
  if (err || !req) return (
    <div style={{ padding: 24 }}>
      <button className="btn btn-ghost" onClick={() => navigate(-1)}>Indietro</button>
      <div className="alert alert-danger" style={{ marginTop: 12 }}>{err || "Non trovata"}</div>
    </div>
  );

  const ticket = req.matches?.[0] ?? null;
  const analysis = (Array.isArray(req.analyses) ? req.analyses[0] : req.analyses) as Record<string, unknown> | null;
  const extracted = (analysis?.extracted_entities ?? null) as Record<string, Record<string, unknown>> | null;
  const incoming = req.incoming_data;
  const extraction = req.extraction as Record<string, unknown> | null;
  // (`isFromConversation` è definita sopra, prima delle early return.)
  const bodyClean = extraction?.body_clean as string | null;
  const unifiedText = extraction?.unified_text as string | null;
  const bodyText = incoming?.body || bodyClean || unifiedText || "";
  const displayHtml = (incoming?.body_html || (extraction?.body_html as string | null)) ?? null;
  // Decodifica entità HTML + collassa righe vuote (email HTML tabellari tipo BCC)
  const collapseLines = (t: string) =>
    t.replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<')
     .replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n))
     .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
     .split('\n').map(l => l.trim()).filter(l => l.length > 0).join('\n').trim();
  const motivo = analysis?.motivo as string | undefined;
  const prioritaRaw = (analysis?.priorita as string | undefined) ?? "";
  const priorita = prioritaRaw.toLowerCase() === "urgente" ? "alta" : prioritaRaw.toLowerCase() === "normale" ? "bassa" : prioritaRaw.toLowerCase() || undefined;
  const tipologia = analysis?.tipologia as string | undefined;
  const causale = preview?.causale ?? ((analysis?.causale as string) || "apertura");
  const numeroRiferimento = preview?.client_ticket_id ?? (analysis?.numero_riferimento as string | undefined);
  const ticketAssociabili = preview?.ticket_associabili ?? ((analysis?.ticket_associabili as any[]) || []);
  const ticketOriginale = (analysis?.ticket_originale as { tipo?: string; idchiamata?: number; request_id?: string; refimpianto?: string; motivo?: string; dataora?: string } | null) ?? null;
  const overall = preview?.overall_confidence ?? (ticket?.overall_confidence ?? 0);
  const em = ticket?.entity_matches ?? {};

  const topAttachmentsRaw = incoming?.attachments ?? [];
  const extractionAttTexts = (extraction?.attachments_text ?? []) as Array<{ filename: string; content_type?: string; size_bytes?: number; parent_eml?: string; extracted_text?: string; method?: string }>;
  // Mappa filename → extracted_text dall'extraction per arricchire gli allegati top-level
  const extTextMap = new Map(extractionAttTexts.filter(a => !a.parent_eml).map(a => [a.filename, a.extracted_text]));
  const topAttachments = topAttachmentsRaw.map(a => ({ ...a, extracted_text: extTextMap.get(a.filename) }));
  const subAttachments = extractionAttTexts.filter(a => a.parent_eml).map(a => ({ filename: a.filename, content_type: a.content_type || "application/octet-stream", size_bytes: a.size_bytes || 0, extracted_text: a.extracted_text }));
  const attachments = [...topAttachments, ...subAttachments];

  // Aggiorna ref con il clientePk effettivo ad ogni render (sempre fresco per doSearch e useEffect).
  effectiveClientePkRef.current =
    corrections.find(c => c.entity_type === "cliente")?.new_pk
    ?? derivedCliente?.pk
    ?? req?.matches?.[0]?.entity_matches?.cliente?.best?.entity?.pk
    ?? undefined;

  const isActionable = ["needs_review", "pending_review", "received"].includes(req.status);
  const isCompleted = ["done", "auto_approved"].includes(req.status);
  // `discarded` non passava da nessuno dei due, e non era una svista innocua: la
  // pratica si apriva senza un solo bottone e **senza "Riapri"**, benché il backend
  // la riapra (`POST /requests/{id}/reopen` accetta done/discarded/error/auto_approved,
  // requests.py:1587). Un vicolo cieco: 238 pratiche su Casadei da cui non si tornava
  // indietro dall'interfaccia.
  const isDiscarded = req.status === "discarded";
  const isClosed = isCompleted || isDiscarded;
  const canEdit = isActionable && !isReadOnly;

  const overrideIsSet = (field: keyof RdiOperatorOverrides) => (
    Object.prototype.hasOwnProperty.call(operatorOverrides, field)
  );
  const currentMotivo = overrideIsSet("motivo") ? (operatorOverrides.motivo ?? "") : (motivo || "");
  const currentPriority = overrideIsSet("priorita") ? (operatorOverrides.priorita ?? "") : (priorita || "");
  const currentNotaOperatore = overrideIsSet("nota_operatore")
    ? (operatorOverrides.nota_operatore ?? "")
    : (req.nota_operatore || "");

  /* ── Button state logic ── */
  const chainCorrections = corrections.filter(c => CHAIN_ENTITIES.has(c.entity_type));
  const needsReprocess = chainCorrections.length > 0 && !reprocessed;
  const canApprove = isActionable && canWork && !isReadOnly && !needsReprocess;
  const canReprocess = isActionable && canWork && !isReadOnly && chainCorrections.length > 0 && !reprocessed && correctionReason !== "";

  /* ── Resolve current values for each entity ── */
  function resolveEntity(key: string) {
    const corr = getCorrection(key);
    const best = em[key]?.best;

    if (key === "cliente") {
      const pk = corr ? corr.new_pk : (derivedCliente ? derivedCliente.pk : (best?.entity?.pk || null));
      const label = corr ? corr.label : (derivedCliente ? derivedCliente.label : (best?.display_label || String(best?.entity?.fields?.ragsoc || "")));
      const fields = corr ? undefined : (derivedCliente?.fields || best?.entity?.fields);
      return { pk, label, fields, modified: !!corr, derived: !corr && !!derivedCliente };
    }
    if (key === "amministratore") {
      const pk = corr ? corr.new_pk : (derivedAdmin ? derivedAdmin.pk : (best?.entity?.pk || null));
      const label = corr ? corr.label : (derivedAdmin ? derivedAdmin.label : (best?.display_label || String(best?.entity?.fields?.ragione_sociale || "")));
      const fields = corr ? undefined : (derivedAdmin?.fields || best?.entity?.fields);
      return { pk, label, fields, modified: !!corr, derived: !corr && !!derivedAdmin };
    }

    if (key === "chiamante") {
      // Un chiamante certificato ha un'identita' DIMOSTRATA (il numero, e la
      // casella su cui ha cliccato) anche quando il nome non lo sappiamo:
      // `display_label` nasce dal template `"{chiamante}"` e in quel caso resta
      // vuoto, e una riga «Nessun match» accanto al badge NUOVO direbbe il
      // falso. Il ripiego sta QUI, in vista, e non nel campo del nome: quel
      // campo a valle diventa nome/cognome nel JSON verso il gestionale del
      // cliente, e un numero di telefono non e' un cognome.
      const pk = corr ? corr.new_pk : (best?.entity?.pk || null);
      const f = best?.entity?.fields;
      const label = corr
        ? corr.label
        : (best?.display_label || String(f?.chiamante || f?.email_certificata || f?.telefono || ""));
      return { pk, label, fields: corr ? undefined : f, modified: !!corr, derived: false };
    }

    const pk = corr ? corr.new_pk : (best?.entity?.pk || null);
    const label = corr ? corr.label : (best?.display_label || "");
    const fields = corr ? undefined : best?.entity?.fields;
    return { pk, label, fields, modified: !!corr, derived: false };
  }

  /* ════════════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════════ */

  /** Render a single entity row in the chain */
  function renderChainRow(key: string, label: string, showArrowBefore: boolean) {
    const data = resolveEntity(key);
    const alts = (em[key]?.alternatives ?? []).filter(a => a.entity?.pk !== data.pk);
    const isOpen = editingEntity === key;
    const color = COLOR[key] || "var(--indaco-gray-600)";

    return (
      <div key={key}>
        {showArrowBefore && <div style={{ paddingLeft: 20, color: "var(--indaco-gray-400)", fontSize: 12, lineHeight: "16px" }}>|</div>}
        {/* Main row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
          background: data.modified ? "var(--indaco-warning-light)" : "var(--indaco-white)",
          border: data.modified ? "1px solid var(--indaco-warning)" : "1px solid transparent",
          borderRadius: 6,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color, flexShrink: 0 }}>{ICON[key]}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: ".03em", minWidth: 72 }}>{label}</span>
          <code style={{ fontFamily: "var(--font-family-mono)", fontSize: 11, color: "var(--indaco-gray-700)", minWidth: 60 }}>
            {data.pk === "NEW" ? <span className="badge badge-warning badge-sm">NUOVO</span> : (stripPkPrefix(data.pk) || "—")}
          </code>
          <span style={{ flex: 1, fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {data.label || <span style={{ color: "var(--indaco-gray-400)" }}>Nessun match</span>}
          </span>
          {data.fields && <span style={{ fontSize: 10, color: "var(--indaco-gray-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{entityDetail(key, data.fields)}</span>}
          {data.fields?.codice_cliente != null && key !== "cliente" && (
            <span className="badge badge-neutral badge-sm" style={{ flexShrink: 0, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={data.fields.cliente_ragsoc ? `${data.fields.codice_cliente} — ${data.fields.cliente_ragsoc}` : String(data.fields.codice_cliente)}>
              {data.fields.cliente_ragsoc ? `${data.fields.codice_cliente} · ${data.fields.cliente_ragsoc}` : `${data.fields.codice_cliente}`}
            </span>
          )}
          {data.modified && <span className="badge badge-warning badge-sm" style={{ flexShrink: 0 }}>CORRETTO</span>}
          {data.derived && !data.modified && <span style={{ fontSize: 9, color: "var(--indaco-gray-500)", flexShrink: 0 }}>derivato</span>}
          {key === "amministratore" && data.pk && adminCoherent !== null && (
            <span className={`badge badge-sm ${adminCoherent ? "badge-success" : "badge-warning"}`} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 2 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>{adminCoherent ? "check" : "warning"}</span>
              {adminCoherent ? "coerente" : "mismatch"}
            </span>
          )}
          {canEdit && (
            <button className="icon-btn" onClick={() => { setEditingEntity(isOpen ? null : key); setSearchQ(""); setSearchResults([]); setAdminResults([]); }}
              style={{ color: isOpen ? "var(--indaco-brand-primary)" : "var(--indaco-gray-500)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isOpen ? "close" : "edit"}</span>
            </button>
          )}
        </div>

        {/* Inline search panel */}
        {isOpen && (
          <div style={{ marginLeft: 20, padding: "8px 12px", background: "var(--indaco-gray-50)", borderRadius: 6, borderLeft: `3px solid ${color}` }}>
            {/* Alternatives from pipeline */}
            {alts.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div className="text-muted" style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Alternative dal sistema</div>
                {alts.map((alt, i) => (
                  <div key={i} onClick={() => selectEntity(key, { pk: alt.entity.pk, label: alt.display_label, fields: alt.entity.fields, score: alt.score })}
                     style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 5, cursor: "pointer", border: "1px solid var(--indaco-gray-200)", background: "var(--indaco-white)", fontSize: 11, marginBottom: 2 }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--indaco-brand-primary-light)"; }} onMouseLeave={e => { e.currentTarget.style.background = "var(--indaco-white)"; }}>
                    <code style={{ fontSize: 10, color: "var(--indaco-gray-600)", minWidth: 55 }}>{stripPkPrefix(alt.entity.pk)}</code>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="fw-semibold">{alt.display_label}</span>
                      {alt.metadata?.review_reason && (
                        <span className="text-xs text-muted" style={{ display: "block" }}>
                          Da verificare: {alt.metadata.review_reason}
                        </span>
                      )}
                    </span>
                    <span style={{ color: "var(--indaco-gray-500)", fontSize: 10 }}>{entityDetail(key, alt.entity.fields)}</span>
                    {(key !== "cliente") && alt.entity.fields?.codice_cliente != null && (
                      <span className="badge badge-neutral badge-sm" style={{ flexShrink: 0, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={alt.entity.fields.cliente_ragsoc ? `${alt.entity.fields.codice_cliente} — ${alt.entity.fields.cliente_ragsoc}` : String(alt.entity.fields.codice_cliente)}>
                        {alt.entity.fields.cliente_ragsoc ? `${alt.entity.fields.codice_cliente} · ${alt.entity.fields.cliente_ragsoc}` : `${alt.entity.fields.codice_cliente}`}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Impianti list — pannello impianto, filtrata per l'edificio corrente */}
            {key === "impianto" && (impiantiLoading || impiantiList.length > 0) && (
              <div style={{ marginBottom: 8 }}>
                <div className="text-muted" style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>
                  Impianti nell'edificio ({impiantiLoading ? "…" : impiantiList.length})
                </div>
                {impiantiList.map(imp => {
                  const impCorr = getCorrection("impianto");
                  const currentImpPk = impCorr ? impCorr.new_pk : (em.impianto?.best?.entity?.pk || "");
                  const isSel = imp.pk === currentImpPk;
                  return (
                    <div key={imp.pk}
                      onClick={canEdit && !isSel ? () => selectEntity("impianto", imp) : undefined}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 5, marginBottom: 2,
                        background: isSel ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)", fontWeight: isSel ? 600 : 400,
                        cursor: canEdit && !isSel ? "pointer" : "default", border: "1px solid var(--indaco-gray-200)", fontSize: 11,
                      }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: isSel ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)" }}>
                        {isSel ? "check_circle" : "radio_button_unchecked"}
                      </span>
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-600)", minWidth: 55 }}>{stripPkPrefix(imp.pk)}</code>
                      <span style={{ fontWeight: 500 }}>{String(imp.fields.desc_tipo || "")}</span>
                      <span style={{ color: "var(--indaco-gray-500)", fontSize: 10 }}>{String(imp.fields.descrizione || "")}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Scoped search — non mostrata per impianto quando la lista precaricata è
                disponibile, né per amministratore (ha il suo blocco HubSpot dedicato). */}
            {key !== "amministratore" && !(key === "impianto" && impiantiList.length > 0) && (() => {
              const clientePkForScope = corrections.find(c => c.entity_type === "cliente")?.new_pk
                ?? derivedCliente?.pk
                ?? req?.matches?.[0]?.entity_matches?.cliente?.best?.entity?.pk
                ?? null;
              const edificioPkForScope = corrections.find(c => c.entity_type === "edificio")?.new_pk
                ?? req?.matches?.[0]?.entity_matches?.edificio?.best?.entity?.pk
                ?? null;
              const scopePk = key === "edificio" ? clientePkForScope
                : key === "impianto" ? edificioPkForScope
                : null;
              const clienteLabelForScope = derivedCliente?.label
                ?? corrections.find(c => c.entity_type === "cliente")?.label
                ?? req?.matches?.[0]?.entity_matches?.cliente?.best?.display_label
                ?? clientePkForScope;
              const scopeLabel = key === "edificio" && scopePk
                ? `Edifici di questo cliente · ${clienteLabelForScope}`
                : key === "impianto" && scopePk
                ? `Impianti di questo edificio · ${scopePk}`
                : null;
              return (<>
                {scopeLabel ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 8px", marginBottom: 6,
                    background: "var(--indaco-brand-primary-light)", border: "1px solid var(--indaco-brand-primary)", borderRadius: 5 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 13, color: "var(--indaco-brand-primary)" }}>filter_alt</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--indaco-brand-primary)" }}>{scopeLabel}</span>
                  </div>
                ) : (
                  <div className="text-muted" style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Ricerca libera</div>
                )}
                <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                  <input className="form-input" autoFocus value={searchQ}
                    onChange={e => { setSearchQ(e.target.value); doSearch(key, e.target.value); }}
                    placeholder={key === "impianto" && scopePk
                      ? "Cerca tra gli impianti dell'edificio selezionato…"
                      : key === "edificio" && scopePk
                      ? "Cerca tra gli edifici del cliente selezionato…"
                      : `Cerca ${label.toLowerCase()}...`}
                    style={{ flex: 1, padding: "5px 8px", fontSize: 11 }} />
                  {searchLoading && <span className="spinner" style={{ width: 14, height: 14, alignSelf: "center" }} />}
                </div>
                {searchResults.length > 0 && searchResults.map(r => (
                  <div key={r.pk} onClick={() => selectEntity(key, r)}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 5, cursor: "pointer", border: "1px solid var(--indaco-gray-200)", background: "var(--indaco-white)", fontSize: 11, marginBottom: 2 }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--indaco-brand-primary-light)"; }} onMouseLeave={e => { e.currentTarget.style.background = "var(--indaco-white)"; }}>
                    <code style={{ fontSize: 10, color: "var(--indaco-gray-600)", minWidth: 55 }}>{stripPkPrefix(r.pk)}</code>
                    <span style={{ flex: 1, fontWeight: 500 }}>{r.label}</span>
                    <span style={{ color: "var(--indaco-gray-500)", fontSize: 10 }}>{entityDetail(key, r.fields)}</span>
                    {(key !== "cliente") && r.fields?.codice_cliente != null && (
                      <span className="badge badge-neutral badge-sm" style={{ flexShrink: 0, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={r.fields.cliente_ragsoc ? `${r.fields.codice_cliente} — ${r.fields.cliente_ragsoc}` : String(r.fields.codice_cliente)}>
                        {r.fields.cliente_ragsoc ? `${r.fields.codice_cliente} · ${r.fields.cliente_ragsoc}` : `${r.fields.codice_cliente}`}
                      </span>
                    )}
                  </div>
                ))}
              </>);
            })()}

            {/* ══ Ricerca amministratore HubSpot (Fase 2) — righe custom con
                n° edifici gestiti + badge di coerenza con l'edificio ══ */}
            {key === "amministratore" && (
              <div>
                <div className="text-muted" style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Cerca amministratore (HubSpot)</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input className="form-input" autoFocus value={searchQ}
                    onChange={e => { setSearchQ(e.target.value); doSearchAdmin(e.target.value); }}
                    placeholder="Ragione sociale amministratore… (min 2 caratteri)"
                    style={{ flex: 1, padding: "5px 8px", fontSize: 11 }} />
                  {adminLoading && <span className="spinner" style={{ width: 14, height: 14, alignSelf: "center" }} />}
                </div>
                {adminResults.map(a => {
                  const coherent = computeAdminCoherence((a.edifici_gestiti ?? []).map(e => e.pk), currentEdificioPk());
                  return (
                    <div key={a.hubspot_id} onClick={() => selectAdmin(a)}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderRadius: 5, cursor: "pointer", border: "1px solid var(--indaco-gray-200)", background: "var(--indaco-white)", fontSize: 11, marginBottom: 2 }}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--indaco-success-light)"; }} onMouseLeave={e => { e.currentTarget.style.background = "var(--indaco-white)"; }}>
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-600)", minWidth: 34 }}>{a.codice_amministratore || "—"}</code>
                      <span style={{ flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                      {a.partita_iva && <span style={{ color: "var(--indaco-gray-500)", fontSize: 10 }}>{a.partita_iva}</span>}
                      <span className="badge badge-neutral badge-sm" style={{ flexShrink: 0 }}>{a.n_edifici} edifici</span>
                      {!a.codice_amministratore ? (
                        <span className="badge badge-warning badge-sm" title="Amministratore senza codice locale: non collegabile all'output" style={{ flexShrink: 0 }}>no codice</span>
                      ) : coherent === true ? (
                        <span className="badge badge-success badge-sm" title="Gestisce l'edificio identificato" style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 2 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>check</span> coerente
                        </span>
                      ) : coherent === false ? (
                        <span className="badge badge-warning badge-sm" title="NON gestisce l'edificio identificato" style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 2 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>warning</span> mismatch
                        </span>
                      ) : null}
                    </div>
                  );
                })}
                {searchQ.trim().length >= 2 && !adminLoading && adminResults.length === 0 && (
                  <div className="text-muted" style={{ fontSize: 10, padding: "4px 2px" }}>Nessun amministratore trovato.</div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    );
  }

  const rdiActionable = isActionable && canWork && !isReadOnly;

  /* Il pannello di certificazione c'è? Serve in due punti (il montaggio e il
     piazzamento degli altri due riquadri): se le due condizioni divergessero,
     la griglia riserverebbe una riga a un pannello che non esiste. */
  const showCertification = Boolean(rdiActionable && isFromConversation && id);
  const certLayout = certificationGridPlacement(certExpanded);
  return (
    <>
      <DetailPageShell
        onBack={() => origin === "mop" ? navigate(queuePath) : navigate(-1)}
        /* Titolo: per una RDI e' l'oggetto dell'email, che dice di cosa si
           tratta. Per una pratica nata da conversazione l'oggetto e'
           sintetico ("Richiesta via whatsapp — draft 1") e in testa alla
           pagina non distingueva una pratica dall'altra: al suo posto il
           motivo dell'analisi, che e' quello che il chiamante ha chiesto. */
        title={(isFromConversation ? (motivo || getCleanSubject(req)) : getCleanSubject(req)) || "Richiesta"}
        titleId={req.id}
        badges={
          <>
            <StatusBadge status={req.status} />
            {origin === "mop" && <span className="badge badge-info">MOP</span>}
            <span className="badge badge-sm" style={{ color: sCol(overall), background: sBg(overall) }}>{pct(overall)}</span>
          </>
        }
        approve={rdiActionable ? { onClick: handleApprove, disabled: !canApprove || actionLoading || !certifyCaller.valid, title: !certifyCaller.valid ? "Completa la scelta sulla certificazione del chiamante" : needsReprocess ? "Riprocessa prima di approvare" : "Approva e invia alla coda" } : undefined}
        reprocess={rdiActionable ? { onClick: () => setShowReprocessModal(true), disabled: !canReprocess || actionLoading || !certifyCaller.valid, title: !certifyCaller.valid ? "Completa la scelta sulla certificazione del chiamante" : !canReprocess ? (reprocessed ? "Già riprocessato" : chainCorrections.length === 0 ? "Modifica cliente, edificio o impianto" : "Seleziona il motivo della correzione") : "Riprocessa con le correzioni" } : undefined}
        reject={rdiActionable ? { onClick: () => setShowRejectModal(true), disabled: actionLoading, label: "Scarta" } : undefined}
        extraActions={rdiActionable ? (
          <>
            {/* Il motivo per cui «Approva» è grigio. Nel pannello è già scritto,
                ma su un cliente da centinaia di edifici l'avviso può stare
                migliaia di pixel sopra o sotto il viewport: qui è l'unico posto
                che l'operatore ha sempre davanti, e non richiede di scoprire un
                `title` passandoci il mouse. */}
            {!certifyCaller.valid && certifyCaller.blockers.length > 0 && (
              <span style={{
                display: "flex", alignItems: "center", gap: 5, maxWidth: 460,
                fontSize: 11, color: "var(--indaco-warning)", alignSelf: "center",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>warning</span>
                <span>
                  Certificazione: {certifyCaller.blockers[0]}
                  {certifyCaller.blockers.length > 1 && ` (+${certifyCaller.blockers.length - 1} altri)`}
                </span>
              </span>
            )}
            {corrections.length > 0 && (
              <button className="btn btn-outline btn-sm" onClick={handleResetAll} disabled={actionLoading} title="Annulla tutte le modifiche alla catena">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>restart_alt</span> Annulla
              </button>
            )}
            {req.previous_matches && (
              <button className="btn btn-outline btn-sm" onClick={handleRevert} disabled={actionLoading}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>undo</span> Originale
              </button>
            )}
            {actionLoading && <span className="spinner" style={{ width: 14, height: 14, alignSelf: "center" }} />}
          </>
        ) : undefined}
      >

      {origin === "mop" && <MopRequestNotice request={req} />}

      {/* Lock banner */}
      {isReadOnly && lockOwner && (
        <div className="alert alert-warning" style={{ marginBottom: 6, fontSize: 10 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>lock</span>
          In lavorazione da <b>{lockOwner}</b> — sola lettura
        </div>
      )}

      {/* Multi-ticket banner */}
      {(req.total_tickets ?? 1) > 1 && (
        <div className="alert alert-warning" style={{ marginBottom: 8, fontSize: 11, flexWrap: "wrap" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>call_split</span>
          <span>Richiesta {(req.ticket_index ?? 0) + 1} di {req.total_tickets}</span>
          {siblings.map(s => (
            <button key={s.id} className="btn btn-outline btn-xs" onClick={() => navigate(`${detailBasePath}/${s.id}`, { replace: true })}>
              {s.ticket_index + 1}/{s.total_tickets} · {s.id.slice(0, 8)}
            </button>
          ))}
        </div>
      )}

      {/* Controllo larghezza pannello EMAIL (punto 8) — stesso pattern del
          controllo Vista dei DDT, ma regola la colonna email invece del PDF. */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", justifyContent: "flex-end", marginBottom: 6 }}>
        <span className="text-xs text-muted">{isFromConversation ? "Vista:" : "Vista email:"}</span>
        {REVIEW3_SPLIT_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            className={emailSplit === p ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"}
            onClick={() => selectEmailSplit(p)}
            title={`Pannello email ${REVIEW3_SPLITS[p].label.toLowerCase()} (${REVIEW3_SPLITS[p].emailPct}%)`}
          >
            {REVIEW3_SPLITS[p].label}
          </button>
        ))}
      </div>

      {/* Body scrollabile (scroll interno) */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {/* ── LAYOUT ridimensionabile: ticket/matching (sx) | EMAIL (dx) ── */}
      <div style={{ display: "grid", gridTemplateColumns: `${100 - REVIEW3_SPLITS[emailSplit].emailPct}fr ${REVIEW3_SPLITS[emailSplit].emailPct}fr`, gap: 12, alignItems: "start", minWidth: 0 }}>

        {/* ═══ Certificazione del chiamante ═══
            Sta in cima, prima del contenuto della pratica, perché la scelta va
            fatta *prima* di approvare: viaggia nello stesso payload. Compare su
            tutte le pratiche conversazionali ancora lavorabili — **anche** se il
            numero risulta già certificato, che è proprio il caso in cui
            l'operatore deve poter togliere o estendere.

            È un figlio **diretto** della griglia, non un contenuto della colonna
            di sinistra, perché da aperto occupa la larghezza piena: il
            piazzamento dei tre riquadri arriva da `certificationGridPlacement`,
            che li tiene coerenti fra loro. Il punto di montaggio è uno solo nei
            due stati — spostarlo fra due rami del JSX lo rimonterebbe da zero,
            buttando via la selezione dell'operatore nel momento in cui allarga
            la tabella. E resta il primo elemento del corpo scrollabile in
            entrambi gli stati, quindi il suo bordo superiore non si muove:
            aprendo e chiudendo, chi guarda non perde il punto. */}
        {showCertification && (
          <div style={certLayout.panel}>
            <CertificationTree
              requestId={id!}
              onChange={setCertifyCaller}
              onExpandedChange={setCertExpanded}
              /* La conversazione la carica già la pagina: passarla evita una
                 seconda fetch della stessa sessione, che darebbe due versioni. */
              conversation={conversationData}
              onOpenConversation={() => {
                document.getElementById("conversazione-completa")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />
          </div>
        )}

        {/* ═══ LEFT ═══ */}
        <div style={{ overflow: "hidden", ...(showCertification ? certLayout.left : { minWidth: 0 }) }}>
          {/* Esito, per primo: chi apre una pratica chiusa deve leggere **perché**
              prima del contenuto. Su una scartata era l'informazione che mancava
              del tutto — la pagina si apriva identica a una ancora da lavorare. */}
          {isClosed && (
            <div style={{ marginBottom: 8 }}>
              <PracticeOutcome
                status={req.status}
                entries={activity}
                pipelineDiscard={req.pipeline_discard}
                notaFallback={isDiscarded ? req.error_message : null}
              />
            </div>
          )}

          <CausaleBadge causale={causale} numeroRiferimento={numeroRiferimento} ticketAssociabili={ticketAssociabili} ticketOriginale={ticketOriginale} isPreview={preview !== null} previewLoading={previewLoading} />

          {/* Motivo + priorità */}
          <div className="card" style={{ marginBottom: 8, padding: "8px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span className="text-muted" style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>Motivo</span>
              {overrideIsSet("motivo") && <span className="badge badge-warning badge-sm">modificato</span>}
              {tipologia && <span className="text-muted" style={{ marginLeft: "auto", fontSize: 10 }}>Tipo: {tipologia}</span>}
            </div>
            <textarea className="form-textarea" value={currentMotivo} onChange={e => setOperatorOverrides(prev => ({ ...prev, motivo: e.target.value }))} readOnly={!canEdit}
              rows={2}
              style={{ fontSize: 11.5, minHeight: "unset", background: canEdit ? "var(--indaco-white)" : "var(--indaco-gray-50)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span className="text-muted" style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>Priorità</span>
              <select className="form-select" value={currentPriority} onChange={e => setOperatorOverrides(prev => ({ ...prev, priorita: e.target.value || null }))} disabled={!canEdit}
                style={{
                  marginLeft: "auto", width: "auto", fontSize: 11, fontWeight: 600, padding: "2px 6px",
                  ...priorityColors(currentPriority),
                }}>
                <option value="">— non impostata —</option>
                <option value="bassa">Bassa</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </select>
            </div>
          </div>

          {/* Nota per il cliente — testo libero incluso nell'output come
              `notaOperatore`, distinto da `note` (LLM), da `approval_note`
              (audit interno) e dalla "Nota operatore" del blocco Motivo
              correzione qui sotto (quella e' il motivo di una correzione di
              match, non finisce mai nell'output). Handoff 2026-07-17. */}
          <div className="card" style={{ marginBottom: 8, padding: "var(--space-2) var(--space-3)" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Nota per il cliente
                {overrideIsSet("nota_operatore") && (
                  <span className="badge badge-warning" style={{ fontSize: 9, padding: "1px 5px" }}>modificato</span>
                )}
              </label>
              <textarea
                className="form-textarea"
                value={currentNotaOperatore}
                onChange={e => setOperatorOverrides(prev => ({ ...prev, nota_operatore: e.target.value }))}
                readOnly={!canEdit}
                rows={2}
                placeholder="Nota libera facoltativa, inclusa nell'output verso il cliente..."
                style={{ minHeight: "unset", fontSize: 11.5 }}
              />
            </div>
          </div>

          {/* ══ CATENA MATCHING ══ */}
          <div className="card" style={{ marginBottom: 10 }}>
            <div className="card-header-accent card-header-accent-neutral">
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--indaco-gray-800)", textTransform: "uppercase", letterSpacing: ".03em" }}>Catena matching</span>
            </div>
            {renderChainRow("cliente", "Cliente", false)}
            {renderChainRow("edificio", "Edificio", true)}
            {renderChainRow("impianto", "Impianto", true)}
          </div>

          {/* Warning chiamante se serve */}
          {chiamanteWarning && (
            <div className="alert alert-danger" style={{ marginBottom: 8, fontSize: 11 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>warning</span>
              {chiamanteWarning}
            </div>
          )}

          {/* ══ ALTRE ENTITÀ (collassabili) ══ */}
          <div className="card" style={{ marginBottom: 10 }}>
            <button className="card-header-accent card-header-accent-neutral" onClick={() => setShowOther(o => !o)}
              style={{ width: "100%", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "var(--indaco-gray-600)" }}>
              Altre entità (Amministratore, Chiamante)
              <span className="material-symbols-outlined" style={{ fontSize: 14, marginLeft: "auto", transition: "transform .2s", transform: showOther ? "rotate(180deg)" : "" }}>expand_more</span>
            </button>
            {showOther && (<>
              {renderChainRow("amministratore", "Amministratore", false)}
              {renderChainRow("chiamante", "Chiamante", false)}
            </>)}
          </div>

          {/* ══ Ragionamento AI + Estratto AI — nella colonna sinistra ══ */}
          <ReasoningPanel em={em} analysis={analysis} extracted={extracted} causale={causale} ticketAssociabili={ticketAssociabili} ticketOriginale={ticketOriginale}
            rdiReasoning={((extraction?.metadata as Record<string, unknown> | undefined)?.rdi_reasoning
              ?? (extraction?.metadata as Record<string, unknown> | undefined)?.v3_reasoning) as RdiReasoning | null | undefined}
            validationErrors={((extraction?.metadata as Record<string, unknown> | undefined)?.rdi_validation_errors
              ?? (extraction?.metadata as Record<string, unknown> | undefined)?.v3_validation_errors) as string[] | undefined} />

          {extracted && Object.keys(extracted).length > 0 && (
            <div className="card" style={{ marginBottom: 8 }}>
              <button
                className="card-header-accent card-header-accent-neutral"
                style={{ width: "100%", border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "var(--indaco-gray-600)", textTransform: "uppercase", letterSpacing: ".04em" }}
                onClick={() => {
                  const el = document.getElementById("ai-extract");
                  if (el) el.style.display = el.style.display === "none" ? "block" : "none";
                }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>data_object</span>
                Dati estratti (dettaglio)
                <span className="material-symbols-outlined" style={{ fontSize: 13, marginLeft: "auto" }}>expand_more</span>
              </button>
              <div id="ai-extract" style={{ display: "none" }}>
                {Object.entries(extracted).map(([eKey, eFields]) => {
                  if (!eFields || !Object.keys(eFields).length) return null;
                  const etConf = entityTypes.find(e => e.key === eKey);
                  return (
                    <div key={eKey} style={{ marginBottom: 5 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--indaco-gray-700)", marginBottom: 1, textTransform: "uppercase" }}>{etConf?.label ?? eKey}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "80px minmax(0,1fr)", gap: "1px 6px", fontSize: 11 }}>
                        {Object.entries(eFields).filter(([, v]) => v != null && String(v).trim()).map(([k, v]) => (
                          <React.Fragment key={k}><span style={{ color: "var(--indaco-gray-500)" }}>{k}</span><span>{String(v)}</span></React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══ Banner mismatch admin / motivo correzione (le azioni sono nella ReviewActionBar sticky in alto) ══ */}
          {isActionable && canWork && !isReadOnly && (adminCoherent === false || needsReprocess) && (
            <div className="card" style={{ padding: "10px 14px" }}>
              {/* Mismatch amministratore↔edificio: warning + due azioni (Fase 2, punto 3) */}
              {adminCoherent === false && (
                <div className="alert alert-warning" style={{ marginBottom: 10, flexDirection: "column", alignItems: "stretch", fontSize: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>warning</span>
                    <span style={{ fontWeight: 600 }}>
                      L'amministratore selezionato{getCorrection("amministratore")?.label ? ` (${getCorrection("amministratore")!.label})` : ""} non gestisce l'edificio identificato{currentEdificioPk() ? ` (${stripPkPrefix(currentEdificioPk()!)})` : ""}.
                    </span>
                  </div>
                  <div style={{ fontSize: 11, marginBottom: 8 }}>
                    Invia comunque i dati (l'output riporterà <code>coerenteConEdificio: false</code>), oppure riprocessa la pratica da capo.
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="btn btn-success btn-sm" onClick={handleApprove} disabled={actionLoading || !certifyCaller.valid}
                      title={!certifyCaller.valid ? "Completa la scelta sulla certificazione del chiamante" : undefined}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>send</span> Invia con dati aggiunti
                    </button>
                    <button className="btn btn-warning btn-sm" onClick={() => setShowReprocessModal(true)} disabled={actionLoading || !certifyCaller.valid}
                      title={!certifyCaller.valid ? "Completa la scelta sulla certificazione del chiamante" : "Rielabora mantenendo tutte le modifiche dell'operatore"}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span> Riprocessa
                    </button>
                    {actionLoading && <span className="spinner" style={{ width: 14, height: 14, alignSelf: "center" }} />}
                  </div>
                </div>
              )}
              {/* Motivo correzione — appare solo se ci sono modifiche alla catena */}
              {needsReprocess && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--indaco-gray-700)", whiteSpace: "nowrap" }}>Motivo correzione:</span>
                    <select className="form-select" value={correctionReason} onChange={e => setCorrectionReason(e.target.value)}
                      style={{ flex: 1, width: "auto", fontSize: 11, padding: "4px 8px", maxWidth: 300 }}>
                      <option value="">— Seleziona —</option>
                      {ENTITY_FEEDBACK_CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    {!correctionReason && <span style={{ fontSize: 10, color: "var(--indaco-warning)" }}>Obbligatorio per riprocessare</span>}
                  </div>
                  <textarea
                    className="form-textarea"
                    placeholder="Nota operatore (opzionale): spiega perché stai correggendo questo match…"
                    value={correctionNote}
                    onChange={e => setCorrectionNote(e.target.value)}
                    style={{ fontSize: 11, minHeight: 52 }}
                  />
                </div>
              )}

              {/* Azioni (Approva/Riprocessa/Scarta + Annulla/Originale) spostate
                  nella ReviewActionBar sticky in alto (fase 3). */}
            </div>
          )}

          {isClosed && canWork && (
            <div style={{ padding: "8px 0" }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowReopenConfirm(true)} disabled={actionLoading}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>undo</span> Riapri
              </button>
            </div>
          )}

          {/* Cronologia in fondo: chi ha fatto cosa, con il dettaglio delle
              modifiche. Il riassunto dell'esito sta in cima — qui c'è la storia
              completa, comprese le riaperture. */}
          <ActivityPanel entries={activity} loading={activityLoading} />
        </div>

        {/* ═══ RIGHT: Email + AI ═══ */}
        <div style={{ position: "sticky", top: 10, ...(showCertification ? certLayout.right : { minWidth: 0 }) }}>
          {/* Sezione supplementare, SOLO per pratiche di origine conversazionale
              (punto 3 del pacchetto E) — non sostituisce nulla, si aggiunge. */}
          {isFromConversation && (
            <div id="conversazione-completa">
            <ConversationSection
              data={conversationData}
              loading={conversationLoading}
              error={conversationError}
              onViewSession={(sessionId, channel) => navigate(channel === "test"
                ? `/settings/agente/sessioni/${encodeURIComponent(sessionId)}`
                : `/conversazioni/sessioni/${encodeURIComponent(sessionId)}`)}
            />
            </div>
          )}
          {/* Provenienza della richiesta.

              Per una RDI e' l'email originale, corpo compreso: e' IL documento
              su cui si decide. Per una pratica nata da conversazione il "corpo"
              e' la trascrizione che il materializer incolla in
              `incoming_data.body` ("Utente: … / Agente: …") — cioe' la STESSA
              conversazione gia' renderizzata qui accanto nel pannello
              «Conversazione», in bolle, con i ruoli e l'ordine dei turni. Era
              la terza copia della stessa cosa nella stessa schermata (la
              seconda era l'estratto dentro il pannello di certificazione).
              Si tiene quella che aiuta davvero chi revisiona — le bolle — e
              qui restano i dati di provenienza, che invece mancavano: chi ha
              scritto, da quale canale, quando, con quali allegati.

              L'oggetto non compare per le conversazioni: e' sintetico
              ("Richiesta via whatsapp — draft 1", scritto dal materializer) e
              cio' che la pratica chiede sta nel blocco «Motivo». */}
          <div className="card" style={{ marginBottom: 8 }}>
            <div className="card-header-accent card-header-accent-neutral">
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{isFromConversation ? "forum" : "mail"}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--indaco-gray-600)", textTransform: "uppercase", letterSpacing: ".04em" }}>{isFromConversation ? "Provenienza" : "Email originale"}</span>
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "50px minmax(0,1fr)", gap: "2px 6px", marginBottom: 6 }}>
                <span style={{ color: "var(--indaco-gray-500)", fontWeight: 600, fontSize: 10 }}>DA</span>
                <span>{getOriginalSenderFull(req) || "—"}</span>
                {isFromConversation ? (
                  <>
                    <span style={{ color: "var(--indaco-gray-500)", fontWeight: 600, fontSize: 10 }}>CANALE</span>
                    <span style={{ fontWeight: 600 }}>{canaleLeggibile(req.channel)}</span>
                  </>
                ) : (
                  <>
                    <span style={{ color: "var(--indaco-gray-500)", fontWeight: 600, fontSize: 10 }}>OGG</span>
                    <span style={{ fontWeight: 600 }}>{getCleanSubject(req) || "—"}</span>
                  </>
                )}
                <span style={{ color: "var(--indaco-gray-500)", fontWeight: 600, fontSize: 10 }}>DATA</span>
                <span>{getOriginalDate(req) ? fmt(getOriginalDate(req)!) : "—"}</span>
              </div>
              {isFromConversation ? (
                <button type="button" className="btn-link" style={{ fontSize: 10, padding: 0 }}
                  onClick={() => document.getElementById("conversazione-completa")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                  Leggi la conversazione ↑
                </button>
              ) : displayHtml ? (
                <iframe srcDoc={displayHtml} sandbox="" title="Email originale"
                  style={{ width: "100%", minHeight: 260, maxHeight: "calc(100vh - 300px)", border: "1px solid var(--indaco-gray-100)", borderRadius: 6, background: "var(--indaco-white)" }} />
              ) : bodyText ? (
                <div style={{ padding: "6px 8px", background: "var(--indaco-gray-50)", borderRadius: 6, fontSize: 11, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "var(--indaco-gray-800)", border: "1px solid var(--indaco-gray-100)", maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}>
                  {collapseLines(bodyText)}
                </div>
              ) : null}
              {attachments.length > 0 && <Attachments requestId={id!} attachments={attachments} />}
            </div>
          </div>


        </div>
      </div>
      </div>

      {/* ── Modals ── */}
      {showRejectModal && <RejectModal onConfirm={handleReject} onCancel={() => setShowRejectModal(false)} />}
      {showReprocessModal && id && (
        <ReprocessModal requestId={id}
          corrections={corrections.map(c => ({
            ticket_index: 0, field_corrected: c.entity_type,
            original_value: em[c.entity_type]?.best?.entity ? { pk: em[c.entity_type]!.best!.entity!.pk } : null,
            corrected_value: { pk: c.new_pk, label: c.label, fields: c.fields },
            correction_category: correctionReason || "manual_correction",
            correction_categories: correctionReason ? [correctionReason] : undefined,
            correction_note: correctionNote || undefined,
          }))}
          correctionNote={correctionNote}
          operatorOverrides={operatorOverrides}
          /* La scelta di certificazione è già stata fatta nella pagina (il
             pannello sta sopra il contenuto): qui viaggia con l'approvazione
             che chiude il riprocesso, e il modale la mostra in chiaro. */
          certifyCaller={certifyCaller.payload}
          certifySummary={certifyCaller.summary}
          onComplete={handleReprocessComplete}
          onCancel={() => setShowReprocessModal(false)}
        />
      )}
      {showReopenConfirm && (
        <ConfirmModal
          title="Riaprire la richiesta?"
          message={<>
            <p style={{ margin: "0 0 8px" }}>La richiesta tornerà in lavorazione. L'output in coda e su Azure Blob verrà cancellato.</p>
            <p style={{ margin: 0, fontStyle: "italic" }}>Se il messaggio è già stato processato dal gestionale, la rimozione potrebbe non avere effetto.</p>
          </>}
          confirmLabel="Riapri e svuota coda"
          cancelLabel="Annulla"
          onConfirm={() => void doReopen()}
          onCancel={() => setShowReopenConfirm(false)}
          loading={actionLoading}
        />
      )}
    </DetailPageShell>
    </>
  );
}
